import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  organizationTagsService,
} from "@/services/organizationTags.service/organizationTags.api";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useState } from "react";

function normalizeTag(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

interface TagPoolFieldProps {
  label?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  id?: string;
}

export function TagPoolField({
  label = "Tags",
  value,
  onChange,
  disabled,
  id = "tag-pool",
}: TagPoolFieldProps) {
  const [pool, setPool] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);

  const loadPool = useCallback(async () => {
    const [list] = await organizationTagsService.list();
    if (list) setPool(list);
  }, []);

  useEffect(() => {
    void loadPool();
  }, [loadPool]);

  const suggestions = useMemo(() => {
    const q = normalizeTag(input);
    if (!q) return pool.slice(0, 20);
    return pool.filter((t) => t.includes(q) && !value.includes(t)).slice(0, 12);
  }, [pool, input, value]);

  const addTag = async (raw: string) => {
    const tag = normalizeTag(raw);
    if (!tag || value.includes(tag)) {
      setInput("");
      setOpen(false);
      return;
    }
    const next = [...value, tag];
    onChange(next);
    setInput("");
    setOpen(false);
    if (!pool.includes(tag)) {
      await organizationTagsService.create(tag);
      setPool((p) => [...new Set([...p, tag])].sort((a, b) => a.localeCompare(b)));
    }
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  return (
    <div className="grid gap-2">
      {label ? (
        <Label htmlFor={id} className="text-sm">
          {label}
        </Label>
      ) : null}
      <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
        {value.map((t) => (
          <Badge key={t} variant="secondary" className="gap-1 pr-1 font-normal">
            {t}
            {!disabled && (
              <button
                type="button"
                className="rounded-sm hover:bg-muted px-0.5 leading-none text-muted-foreground hover:text-foreground"
                onClick={() => removeTag(t)}
                aria-label={`Remover ${t}`}
              >
                ×
              </button>
            )}
          </Badge>
        ))}
      </div>
      <div className="relative">
        <Input
          id={id}
          disabled={disabled}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void addTag(input);
            }
          }}
          placeholder="Digite uma tag e Enter"
          autoComplete="off"
          className="text-foreground"
        />
        {open && !disabled && (suggestions.length > 0 || normalizeTag(input)) ? (
          <ul
            className={cn(
              "absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md",
              "text-sm"
            )}
            role="listbox"
          >
            {suggestions.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground"
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    void addTag(s);
                  }}
                >
                  {s}
                </button>
              </li>
            ))}
            {normalizeTag(input) && !value.includes(normalizeTag(input)) ? (
              <li>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-primary hover:bg-accent"
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    void addTag(input);
                  }}
                >
                  Adicionar “{normalizeTag(input)}”
                </button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">
        Tags da organização (mesmo pool para clientes e tickets).
      </p>
    </div>
  );
}
